import React from 'react';
import {useTranslation} from 'react-i18next';

// Shared player list panel used in 3 layout slots:
//   - desktop right sidebar (variant="desktop")
//   - portrait mobile / spectator inline panel (variant="compact")
//   - landscape mobile chat sidebar (variant="compact")
//
// Behaviour mirrors the previous inline copies in Drawing.js.
const COMPACT_SIZES = {gap: 3, padding: '4px 4px', font: 11, dot: 6, icon: 10, badge: 7};
const DESKTOP_SIZES = {gap: 6, padding: '6px 8px', font: 13, dot: 8, icon: 11, badge: 8};

export default function PlayersPanel(props) {
    const {t} = useTranslation();
    const {
        variant = 'desktop',
        currentUser, roomOwner,
        isGuessMode, guessUserList, users,
        guessRound, isSpectator, everyoneDraws, selfOptedIn,
        toggleOptIn, onKickTarget,
        bodyMaxHeight,
        showOptInInHeader = true,
        wrapperStyle, bodyStyle,
        selfAfk, toggleAfk,
        aiAvailable, aiAllowed, toggleAiAllowed,
    } = props;

    const compact = variant === 'compact';
    const sizes = compact ? COMPACT_SIZES : DESKTOP_SIZES;

    const canOptIn = isGuessMode && !isSpectator && !everyoneDraws
        && !(guessRound && guessRound.active && guessRound.drawer === currentUser);
    const showHeaderOptInButton = canOptIn && showOptInInHeader && !compact;
    const showCompactOptInButton = canOptIn && compact;

    // AFK button: visible in guess mode for non-spectators in the header.
    const showAfkButton = isGuessMode && !isSpectator && !!toggleAfk;
    const showAiToggle = isGuessMode && aiAvailable !== false && currentUser && roomOwner && currentUser === roomOwner && !!toggleAiAllowed;
    const aiIsAllowed = aiAllowed !== false;
    const hasHeaderControls = showAiToggle || showAfkButton || showHeaderOptInButton;

    const handleClick = (name) => () => {
        if (name !== currentUser && onKickTarget) onKickTarget(name);
    };

    const rows = isGuessMode
        ? [...(guessUserList || [])]
            .sort((a, b) => (b.points || 0) - (a.points || 0))
            .map((u, i) => (
                <div key={u.name || i} data-player-name={u.name || ''} data-player-is-bot={u.isBot ? 'true' : 'false'} data-player-spectator={u.spectator ? 'true' : 'false'} data-player-afk={u.afk ? 'true' : 'false'} onClick={handleClick(u.name)} style={{
                    display: 'flex', alignItems: 'center', gap: sizes.gap,
                    padding: sizes.padding, fontSize: sizes.font, color: '#eee',
                    cursor: u.name !== currentUser ? 'pointer' : 'default',
                    opacity: u.afk ? 0.55 : 1,
                    background: guessRound && guessRound.drawer === u.name
                        ? 'rgba(255,255,255,0.2)' : 'transparent',
                }}>
                    <span style={{
                        width: sizes.dot, height: sizes.dot, borderRadius: '50%', flexShrink: 0,
                        background: u.optedIn ? '#6c6' : '#666',
                    }}/>
                    {guessRound && guessRound.active && guessRound.drawer === u.name && (
                        <span style={{fontSize: sizes.icon, flexShrink: 0}}>✏️</span>
                    )}
                    <span style={{
                        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', textAlign: 'left',
                        color: u.guessedCorrect ? '#6c6' : u.name === currentUser ? '#7ef7ff' : '#eee',
                    }}>
                        {roomOwner === u.name && '👑 '}{u.name}
                        {u.isBot && <span className="ChatAiBadge" style={{marginLeft: 3, fontSize: sizes.badge}}>AI</span>}
                        {u.spectator && <span style={{marginLeft: 3, fontSize: sizes.badge, background: '#666', color: '#ccc', borderRadius: 3, padding: '0 3px'}}>TV</span>}
                        {u.afk && <span style={{marginLeft: 3, fontSize: sizes.badge, background: '#cc8844', color: '#fff', borderRadius: 3, padding: '0 3px', fontWeight: 'bold'}}>{t('drawing.afk') || 'AFK'}</span>}
                    </span>
                    {u.guessedCorrect && (
                        <span style={{fontSize: sizes.icon, color: '#6c6', flexShrink: 0}}>✓</span>
                    )}
                    <span style={{fontSize: sizes.icon, color: '#ffd666', marginLeft: 'auto', flexShrink: 0}}>
                        {u.points || 0} {t('drawing.pts')}
                    </span>
                </div>
            ))
        : (users || []).map((u, i) => (
            <div key={u.name || i} data-player-name={u.name || ''} data-player-is-bot={u.isBot ? 'true' : 'false'} onClick={handleClick(u.name)} style={{
                display: 'flex', alignItems: 'center', gap: sizes.gap,
                padding: sizes.padding, fontSize: sizes.font, color: '#eee',
                cursor: u.name !== currentUser ? 'pointer' : 'default',
            }}>
                <span style={{
                    width: sizes.dot, height: sizes.dot, borderRadius: '50%', flexShrink: 0,
                    background: '#666',
                }}/>
                <span style={{flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left'}}>
                    {!compact && roomOwner === u.name && '👑 '}{u.name}
                    {u.isBot && <span className="ChatAiBadge" style={{marginLeft: 3, fontSize: sizes.badge}}>AI</span>}
                </span>
            </div>
        ));

    const defaultWrapper = compact
        ? {background: 'lightslategray', border: '1px solid #888', display: 'flex', flexDirection: 'column', fontSize: 11}
        : {width: 225, border: '2px solid #ccc', background: 'rgba(119,136,153,0.55)', marginBottom: 3,
           boxShadow: '1px 1px 4px 3px rgba(0,0,0,0.2)', backdropFilter: 'blur(6px)'};
    const defaultHeader = compact
        ? {fontSize: 11, textAlign: 'left', background: '#595279', padding: '3px 6px', color: '#eee',
           display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}
        : {fontSize: 14, textAlign: 'left', background: 'rgba(89,82,121,0.75)', padding: '.3em .6em', color: '#eee',
           display: 'flex', justifyContent: 'flex-end', alignItems: 'center'};
    const defaultBody = compact
        ? {flex: 1, overflowY: 'auto', overflowX: 'hidden'}
        : {maxHeight: bodyMaxHeight || 120, overflowY: 'auto'};

    return (
        <div data-players-panel="true" style={{...defaultWrapper, ...wrapperStyle}}>
            {hasHeaderControls && <div style={defaultHeader}>
                <span style={{display: 'flex', gap: 4, alignItems: 'center'}}>
                    {showAiToggle && (
                        <button
                            data-ai-access-toggle="true"
                            data-ai-allowed={aiIsAllowed ? 'true' : 'false'}
                            aria-pressed={aiIsAllowed}
                            onClick={toggleAiAllowed}
                            title={aiIsAllowed ? t('drawing.acceptAiTitle', 'AI players can join') : t('drawing.blockAiTitle', 'AI players cannot join')}
                            style={{
                                fontSize: 10, padding: '2px 6px', cursor: 'pointer',
                                background: aiIsAllowed ? '#4a8a4a' : '#8a4a4a',
                                color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                                borderRadius: 4, fontWeight: 'bold', flexShrink: 0,
                            }}
                        >
                            {aiIsAllowed ? t('drawing.acceptAi', 'AI allowed') : t('drawing.blockAi', 'AI blocked')}
                        </button>
                    )}
                    {showAfkButton && (
                        <button
                            onClick={() => toggleAfk(!selfAfk)}
                            title={selfAfk ? (t('drawing.afkBack') || 'I\'m back') : (t('drawing.afkAway') || 'Mark me away')}
                            style={{
                                fontSize: 10, padding: '2px 6px', cursor: 'pointer',
                                background: selfAfk ? '#cc8844' : 'transparent',
                                color: selfAfk ? '#fff' : '#ddd',
                                border: '1px solid ' + (selfAfk ? '#ffaa55' : 'rgba(255,255,255,0.3)'),
                                borderRadius: 4, fontWeight: 'bold', flexShrink: 0,
                            }}
                        >
                            {selfAfk ? (t('drawing.afkBack') || 'Back') : (t('drawing.afkAway') || 'AFK')}
                        </button>
                    )}
                    {showHeaderOptInButton && (
                        <button
                            data-guess-draw-opt-in="true"
                            data-guess-opt-in-button="true"
                            data-guess-opted-in={selfOptedIn ? 'true' : 'false'}
                            data-guess-opt-in-state={selfOptedIn ? 'opted-in' : 'not-opted-in'}
                            onClick={toggleOptIn}
                            style={{
                                fontSize: 10, padding: '2px 6px', cursor: 'pointer',
                                background: selfOptedIn ? '#4a8a4a' : '#7755cc',
                                color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 4,
                                fontWeight: 'bold', flexShrink: 0,
                            }}
                        >
                            {selfOptedIn ? t('drawing.dontDraw') : t('drawing.draw')}
                        </button>
                    )}
                </span>
            </div>}
            <div data-players-list="true" style={{...defaultBody, ...bodyStyle}}>
                {rows}
            </div>
            {showCompactOptInButton && (
                <button
                    data-guess-draw-opt-in="true"
                    data-guess-opt-in-button="true"
                    data-guess-opted-in={selfOptedIn ? 'true' : 'false'}
                    data-guess-opt-in-state={selfOptedIn ? 'opted-in' : 'not-opted-in'}
                    onClick={toggleOptIn}
                    style={{
                        width: '100%', padding: '8px 6px', cursor: 'pointer',
                        background: selfOptedIn ? '#4a8a4a' : '#7755cc',
                        color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 0,
                        borderLeft: 'none', borderRight: 'none', borderBottom: 'none',
                        fontSize: 13, lineHeight: 1.15, fontWeight: 'bold', flexShrink: 0,
                        boxSizing: 'border-box', appearance: 'none', WebkitAppearance: 'none',
                    }}
                >
                    {selfOptedIn ? t('drawing.dontDraw') : t('drawing.draw')}
                </button>
            )}
        </div>
    );
}
